import React from "react";
import { Link } from "react-router-dom";
import { FaBalanceScale, FaCheckCircle, FaMapMarkerAlt, FaShieldAlt, FaStar } from "react-icons/fa";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  formatMetric,
  getCapacity,
  getCardImage,
  getCompletedOrders,
  getManufacturerLocation,
  getMoqSummary,
  getRatingDisplay,
  getResponseTime,
  isFeaturedManufacturer,
} from "../../utils/contractManufacturerCardHelpers";
import MarketplaceProductShareButton from "./MarketplaceProductShareButton";

function buildSpecItems(item, dimensionUnitOptions) {
  return [
    { label: "Capacity", value: formatMetric(getCapacity(item)) },
    { label: "Response", value: formatMetric(getResponseTime(item)) },
    { label: "Orders", value: formatMetric(getCompletedOrders(item)) },
    { label: "MOQ", value: formatMetric(getMoqSummary(item, dimensionUnitOptions)) },
  ];
}

function resolveRibbon(item) {
  const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";
  if (isVerified) {
    return { label: "VERIFIED", className: "marketplace-product-grid-card__ribbon--certified" };
  }
  if (isFeaturedManufacturer(item)) {
    return { label: "FEATURED", className: "marketplace-product-grid-card__ribbon--seller" };
  }
  return null;
}

export default function ContractManufacturerGridCard({
  item,
  dimensionUnitOptions = [],
  rfqSent = false,
  onToggleCompare,
  compared = false,
}) {
  const profileHref = `/contract-manufacturers/profile/${item.company_id}`;
  const rfqHref = `/contract-manufacturers/rfq/${item.company_id}`;
  const imageUrl = getCardImage(item);
  const location = getManufacturerLocation(item);
  const specItems = buildSpecItems(item, dimensionUnitOptions);
  const ribbon = resolveRibbon(item);
  const rating = getRatingDisplay(item);
  const isVerified = String(item?.verification_status || "").toLowerCase() === "verified";

  return (
    <article className="marketplace-product-grid-card marketplace-product-grid-card--cm">
      <div className="marketplace-product-grid-card__media">
        {ribbon ? (
          <span className={`marketplace-product-grid-card__ribbon ${ribbon.className}`}>{ribbon.label}</span>
        ) : null}
        <Link to={profileHref} className="marketplace-product-grid-card__media-link">
          {imageUrl ? (
            <img src={resolvePublicFileUrl(imageUrl)} alt={item.company_name || "Manufacturer"} loading="lazy" />
          ) : (
            <div className="marketplace-product-grid-card__no-image">No image</div>
          )}
        </Link>
      </div>

      <div className="marketplace-product-grid-card__body">
        <Link to={profileHref} className="marketplace-product-grid-card__title">
          {item.company_name || "Manufacturer"}
        </Link>

        <div className="marketplace-product-grid-card__meta">
          <span className="marketplace-product-grid-card__company">Contract Manufacturer</span>
          {isVerified ? (
            <span className="marketplace-product-grid-card__verified">
              <FaCheckCircle aria-hidden /> Verified
            </span>
          ) : null}
          {location ? (
            <span className="marketplace-product-grid-card__location">
              <FaMapMarkerAlt aria-hidden /> {location}
            </span>
          ) : null}
        </div>

        <div className="marketplace-product-grid-card__price-row">
          {rating ? (
            <p className="marketplace-product-grid-card__rating">
              <FaStar aria-hidden /> {rating}
            </p>
          ) : null}
        </div>

        {specItems.length ? (
          <dl className="marketplace-product-grid-card__specs">
            {specItems.map((spec) => (
              <div key={`${spec.label}-${spec.value}`} className="marketplace-product-grid-card__spec">
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>

      <div className="marketplace-product-grid-card__actions">
        <div className="marketplace-product-grid-card__actions-primary">
          {rfqSent ? (
            <button type="button" className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--primary" disabled>
              <FaShieldAlt aria-hidden /> RFQ Sent
            </button>
          ) : (
            <Link to={rfqHref} className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--primary">
              Request for Quote
            </Link>
          )}
          <Link to={profileHref} className="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--outline-dark">
            View Profile
          </Link>
        </div>
        <div className="marketplace-product-grid-card__actions-secondary">
          <button
            type="button"
            className={`marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost${compared ? " is-active" : ""}`}
            onClick={() => onToggleCompare?.(item.company_id)}
          >
            <FaBalanceScale aria-hidden /> Compare
          </button>
          <MarketplaceProductShareButton
            productId={item.company_id}
            title={item.company_name || "Contract Manufacturer"}
            sharePath={profileHref}
            imageUrl={imageUrl ? resolvePublicFileUrl(imageUrl) : ""}
            companyName={item.company_name || ""}
            description={`Contract manufacturer profile${location ? ` — ${location}` : ""}`}
            className="marketplace-product-grid-card__share"
            triggerClassName="marketplace-product-grid-card__btn marketplace-product-grid-card__btn--ghost marketplace-product-share__trigger"
          />
        </div>
      </div>
    </article>
  );
}
